import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge, Table } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaPrescriptionBottle, FaCalendarAlt, FaPills, FaFileAlt, FaTrashAlt, FaShoppingCart } from "react-icons/fa";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import moment from "moment";
import Swal from 'sweetalert2';

export default function PetInhousePrescriptionComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPrescription, setEditingPrescription] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [generalNote, setGeneralNote] = useState("");
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [hasPrescriptionForAppointment, setHasPrescriptionForAppointment] = useState(false);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // Edit permission for prescription
    const canEditPrescription = () => {
        return hasPermission("Appointment Prescription edit");
    };

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const getTodayDateTimeLocal = () => {
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const local = new Date(now.getTime() - offset * 60000);
        return local.toISOString().slice(0, 16);
    };

    useEffect(() => {
        fetchPrescriptions();
    }, [petId, appointmentId]);

    const fetchPrescriptions = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/inhouse/prescriptions/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status && response.data.data) {
                const prescriptions = response.data.data;
                setPrescriptions(prescriptions);
                const hasPrescription = prescriptions.some(prescription =>
                    prescription.appointment_id === appointmentId
                );
                setHasPrescriptionForAppointment(hasPrescription);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            } else {
                setPrescriptions([]);
            }
        } catch (error) {
            console.error("Error fetching prescriptions:", error);
            showErrorToast(t('failed_to_load_prescriptions'));
            setPrescriptions([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/inhouse/prescriptions/create`,
                {},
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status && response.data.data) {
                setAvailableProducts(response.data.data.products || []);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            showErrorToast(t('failed_to_load_products'));
        } finally {
            setLoadingProducts(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const formatTimeDisplay = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const [hours, minutes] = timeString.split(':');
            let hr = parseInt(hours, 10);
            const ampm = hr >= 12 ? 'PM' : 'AM';
            hr = hr % 12 || 12;
            return `${hr}:${minutes} ${ampm}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handleAddPrescription = async () => {
        if (!canEditPrescription()) {
            showWarningToast(t('you_dont_have_permission_to_add'));
            return;
        }
        setEditingPrescription(null);
        setSelectedProducts([]);
        setGeneralNote("");
        await fetchProducts();
        setShowModal(true);
    };

    const handleEditPrescription = async (prescription) => {
        if (!canEditPrescription()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }
        setEditingPrescription(prescription);

        // Map existing prescription details to the format expected by the form
        const existingProducts = prescription.items?.map((item, index) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name,
            used_qty: item.used_qty,
            unit_type: item.unit_type || '',
            notes: item.notes || '',
            isNew: false
        })) || [];

        setSelectedProducts(existingProducts);
        setGeneralNote(prescription.notes || "");
        await fetchProducts();
        setShowModal(true);
    };

    const handleDeletePrescription = async (prescriptionId) => {
        if (!canDelete()) {
            showWarningToast(t('only_admin_can_delete'));
            return;
        }

        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!") + " " + t('Stock will be restored automatically.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    setSubmitting(true);
                    const AuthToken = localStorage.getItem("authToken");
                    const response = await axios.delete(
                        `${process.env.REACT_APP_API_URL}/inhouse/prescriptions/${prescriptionId}`,
                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                    );

                    if (response.data.status) {
                        await fetchPrescriptions();
                        showSuccessToast(t('Prescription deleted successfully and stock restored!'));
                        return true;
                    } else {
                        showErrorToast(response.data.message || t('failed_to_delete_prescription'));
                        return false;
                    }
                } catch (error) {
                    console.error("Error deleting prescription:", error);
                    showErrorToast(error.response?.data?.message || t('failed_to_delete_prescription'));
                    return false;
                } finally {
                    setSubmitting(false);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    };

    const handleAddNewRow = () => {
        const newId = Date.now();
        setSelectedProducts([...selectedProducts, {
            id: newId,
            product_id: '',
            product_name: '',
            used_qty: '',
            unit_type: '',
            isNew: true
        }]);
    };

    const handleProductSelect = (productId, rowId) => {
        const selectedProduct = availableProducts.find(p => p.id === parseInt(productId));

        // Check if product is already selected in another row
        const isAlreadySelected = selectedProducts.some(p => p.product_id === productId && p.id !== rowId);

        if (isAlreadySelected) {
            showWarningToast(t('This product is already added to the prescription'));
            return;
        }

        setSelectedProducts(selectedProducts.map(p =>
            p.id === rowId ? {
                ...p,
                product_id: productId,
                product_name: selectedProduct?.name || '',
                unit_type: selectedProduct?.unit_type || selectedProduct?.purchase_unit || '',
                used_qty: ''
            } : p
        ));
    };

    const handleUpdateQuantity = (rowId, newQuantity) => {
        if (newQuantity !== '' && parseFloat(newQuantity) < 0.01) {
            setSelectedProducts(selectedProducts.filter(p => p.id !== rowId));
        } else {
            setSelectedProducts(selectedProducts.map(p =>
                p.id === rowId ? { ...p, used_qty: newQuantity } : p
            ));
        }
    };

    const handleRemoveProduct = (rowId) => {
        setSelectedProducts(selectedProducts.filter(p => p.id !== rowId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Filter out products without product_id or quantity
        const validProducts = selectedProducts.filter(p => p.product_id && p.used_qty && p.used_qty !== '' && parseFloat(p.used_qty) > 0);

        if (validProducts.length === 0) {
            showWarningToast(t('please_add_at_least_one_product_with_quantity'));
            return;
        }

        // Check stock availability for new/updated items
        for (const product of validProducts) {
            if (product.product_id) {
                const selectedProduct = availableProducts.find(p => p.id === parseInt(product.product_id));
                const quantity = parseFloat(product.used_qty);
                if (selectedProduct && selectedProduct.stock_qty < quantity) {
                    showErrorToast(t(`Insufficient stock for ${product.product_name}! Available: ${selectedProduct.stock_qty} ${product.unit_type || 'units'}`));
                    return;
                }
            }
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingPrescription
                ? `${process.env.REACT_APP_API_URL}/inhouse/prescriptions/update/${editingPrescription.id}`
                : `${process.env.REACT_APP_API_URL}/inhouse/prescriptions`;

            // Prepare payload matching backend expectations
            const payload = {
                appointment_id: appointmentId,
                prescription_date: getTodayDateTimeLocal(),
                notes: generalNote || null,
                items: validProducts.map(p => ({
                    id: p.id && !p.isNew ? p.id : undefined,
                    product_id: parseInt(p.product_id),
                    used_qty: parseFloat(p.used_qty),
                    notes: null
                }))
            };

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.status) {
                setShowModal(false);
                fetchPrescriptions();
                showSuccessToast(editingPrescription ? t('Prescription updated successfully!') : t('Prescription created successfully!'));
            }
        } catch (error) {
            console.error("Error saving prescription:", error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                showErrorToast(errorMessages.join(', '));
            } else if (error.response?.data?.message) {
                showErrorToast(error.response.data.message);
            } else {
                showErrorToast(t('failed_to_save_prescription'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getProductOptions = (currentRowId) => {
        const selectedProductIds = selectedProducts
            .filter(p => p.id !== currentRowId && p.product_id)
            .map(p => parseInt(p.product_id));

        return availableProducts
            .filter(product => !selectedProductIds.includes(product.id))
            .map(product => ({
                value: product.id,
                label: `${product.name} (Stock: ${product.stock_qty} ${product.unit_type || product.purchase_unit || 'units'})`,
                stock: product.stock_qty,
                unit_type: product.unit_type || product.purchase_unit || ''
            }));
    };

    const groupPrescriptionsByAppointment = () => {
        const grouped = {};
        prescriptions.forEach(prescription => {
            const appointmentIdKey = prescription.appointment_id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: {
                        id: prescription.appointment_id,
                        appointment_number: prescription.appointment_number,
                        doctor_name: prescription.doctor_name,
                        customer_name: prescription.customer_name,
                        pet_name: prescription.pet_name
                    },
                    prescriptions: []
                };
            }
            grouped[appointmentIdKey].prescriptions.push(prescription);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading prescriptions')}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted d-flex align-items-center gap-2">
                    <FaPrescriptionBottle className="text-primary" />
                    {t('in house prescriptions list')}
                </h6>
                {isAppointmentOpen && !hasPrescriptionForAppointment &&
                    (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddPrescription}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add in house prescription')}
                        </Button>
                    )}
            </div>

            {!prescriptions || prescriptions.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaPills size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no in house prescriptions found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="inhouse-prescriptions-accordion"
                >
                    {Object.entries(groupPrescriptionsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, prescriptions: appointmentPrescriptions } = appointmentData;

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>

                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentPrescriptions.length} {t('prescription(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-prescriptions-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentPrescriptions.map((prescription) => {
                                            if (!prescription) return null;
                                            const dateTime = formatDateTime(prescription.prescription_date || prescription.created_at);

                                            return (
                                                <Accordion.Item eventKey={String(prescription.id)} key={prescription.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <FaPrescriptionBottle className="text-primary" />
                                                                <Badge bg="info" className="px-3 py-2">
                                                                    {prescription.items?.length || 0} {t('items')}
                                                                </Badge>
                                                                <div className="text-muted small">
                                                                    👨‍⚕️ <strong>Doctor:</strong> {prescription?.doctor_name || 'Unknown Doctor'}
                                                                </div>
                                                                {dateTime && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {dateTime.date}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime size={14} />
                                                                            {formatTimeDisplay(dateTime.time)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                {canEditPrescription() && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditPrescription(prescription)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeletePrescription(prescription.id)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>
                                                                <div className="p-3 bg-light rounded" style={{ overflowX: 'auto' }}>
                                                                    <Table striped bordered hover responsive>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{t('Product Name')}</th>
                                                                                <th>{t('Quantity')}</th>
                                                                                <th>{t('Unit')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {prescription.items?.map((item, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>{item.product_name}</td>
                                                                                    <td>{item.used_qty}</td>
                                                                                    <td>{item.unit_type || '-'}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>

                                                                    {prescription.notes && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{prescription.notes}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" style={{ '--bs-modal-width': '1200px' }}>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        {editingPrescription ? t('Edit In House Prescription') : t('Add In House Prescription')}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {/* Selected Products Table */}
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">
                                    <FaPills className="text-success me-2" />
                                    {t('Prescription Items')}
                                </h6>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddNewRow}
                                    disabled={loadingProducts}
                                >
                                    <FaPlus size={12} className="me-1" /> {t('Add Medicine')}
                                </Button>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <Table bordered hover responsive>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '50%' }}>{t('Medicine Name')}</th>
                                            <th style={{ width: '25%' }}>{t('Quantity')}</th>
                                            <th style={{ width: '20%' }}>{t('Unit')}</th>
                                            <th style={{ width: '5%' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProducts.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted py-4">
                                                    <FaShoppingCart size={30} className="mb-2 d-block mx-auto" />
                                                    {t('No products added. Click "Add Medicine" to add items.')}
                                                </td>
                                            </tr>
                                        ) : (
                                            selectedProducts.map((product) => (
                                                <tr key={product.id}>
                                                    <td>
                                                        <Form.Select
                                                            size="sm"
                                                            value={product.product_id}
                                                            onChange={(e) => handleProductSelect(e.target.value, product.id)}
                                                            disabled={loadingProducts}
                                                        >
                                                            <option value="">{t('Select product')}</option>
                                                            {getProductOptions(product.id).map(opt => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            min="0.01"
                                                            step="0.01"
                                                            value={product.used_qty === '' ? '' : product.used_qty}
                                                            onChange={(e) => handleUpdateQuantity(product.id, e.target.value)}
                                                            placeholder={t('Enter quantity')}
                                                            style={{ textAlign: 'center' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            readOnly
                                                            value={product.unit_type}
                                                            placeholder={t('Unit')}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveProduct(product.id)}
                                                        >
                                                            <FaTrashAlt size={12} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>

                        {/* Note Field */}
                        <div>
                            <Form.Label className="fw-bold">
                                <FaFileAlt className="me-2" /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="p-2">
                        <Button
                            variant="secondary"
                            onClick={() => setShowModal(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting || selectedProducts.filter(p => p.product_id && p.used_qty && p.used_qty !== '' && parseFloat(p.used_qty) > 0).length === 0}
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingPrescription ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}